import React, { useEffect, useRef, useState } from 'react';
import type { BusinessCard } from '../lib/types';
import { cardToGltf } from '../lib/gltf';

type Gltf = ReturnType<typeof cardToGltf>['json'];

interface CardGltfViewerProps {
  card: BusinessCard;
  className?: string;
}

function loadImage(uri: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = (err) => reject(err);
    img.src = uri;
  });
}

function getBase64Data(uri: string) {
  const comma = uri.indexOf(',');
  const data = uri.slice(comma + 1);
  return atob(data);
}

function decodeBuffer(bufferUri: string) {
  const binary = getBase64Data(bufferUri);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

export function CardGltfViewer({ card, className = '' }: CardGltfViewerProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hovering, setHovering] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl = canvas.getContext('webgl');
    if (!gl) {
      setError('WebGL not available');
      return;
    }

    let raf = 0;
    let texture: WebGLTexture | null = null;
    let program: WebGLProgram | null = null;
    let vao: WebGLBuffer | null = null;
    let ibo: WebGLBuffer | null = null;

    const resize = () => {
      const { clientWidth, clientHeight } = canvas;
      if (canvas.width !== clientWidth || canvas.height !== clientHeight) {
        canvas.width = clientWidth;
        canvas.height = clientHeight;
      }
      gl.viewport(0, 0, canvas.width, canvas.height);
    };
    resize();

    const gltf = cardToGltf(card).json as Gltf;

    const multiplyMat4 = (a: Float32Array, b: Float32Array) => {
      // Column-major multiplication: out = a * b
      const out = new Float32Array(16);
      const a00 = a[0], a01 = a[1], a02 = a[2], a03 = a[3];
      const a10 = a[4], a11 = a[5], a12 = a[6], a13 = a[7];
      const a20 = a[8], a21 = a[9], a22 = a[10], a23 = a[11];
      const a30 = a[12], a31 = a[13], a32 = a[14], a33 = a[15];

      const b00 = b[0], b01 = b[1], b02 = b[2], b03 = b[3];
      const b10 = b[4], b11 = b[5], b12 = b[6], b13 = b[7];
      const b20 = b[8], b21 = b[9], b22 = b[10], b23 = b[11];
      const b30 = b[12], b31 = b[13], b32 = b[14], b33 = b[15];

      out[0] = b00 * a00 + b01 * a10 + b02 * a20 + b03 * a30;
      out[1] = b00 * a01 + b01 * a11 + b02 * a21 + b03 * a31;
      out[2] = b00 * a02 + b01 * a12 + b02 * a22 + b03 * a32;
      out[3] = b00 * a03 + b01 * a13 + b02 * a23 + b03 * a33;

      out[4] = b10 * a00 + b11 * a10 + b12 * a20 + b13 * a30;
      out[5] = b10 * a01 + b11 * a11 + b12 * a21 + b13 * a31;
      out[6] = b10 * a02 + b11 * a12 + b12 * a22 + b13 * a32;
      out[7] = b10 * a03 + b11 * a13 + b12 * a23 + b13 * a33;

      out[8] = b20 * a00 + b21 * a10 + b22 * a20 + b23 * a30;
      out[9] = b20 * a01 + b21 * a11 + b22 * a21 + b23 * a31;
      out[10] = b20 * a02 + b21 * a12 + b22 * a22 + b23 * a32;
      out[11] = b20 * a03 + b21 * a13 + b22 * a23 + b23 * a33;

      out[12] = b30 * a00 + b31 * a10 + b32 * a20 + b33 * a30;
      out[13] = b30 * a01 + b31 * a11 + b32 * a21 + b33 * a31;
      out[14] = b30 * a02 + b31 * a12 + b32 * a22 + b33 * a32;
      out[15] = b30 * a03 + b31 * a13 + b32 * a23 + b33 * a33;
      return out;
    };

    const perspective = (fov: number, aspect: number, near: number, far: number) => {
      const f = 1.0 / Math.tan(fov / 2);
      const nf = 1 / (near - far);
      const out = new Float32Array(16);
      out[0] = f / aspect;
      out[5] = f;
      out[10] = (far + near) * nf;
      out[11] = -1;
      out[14] = (2 * far * near) * nf;
      return out;
    };

    const rotateX = (angle: number) => {
      const s = Math.sin(angle);
      const c = Math.cos(angle);
      return new Float32Array([
        1, 0, 0, 0,
        0, c, s, 0,
        0, -s, c, 0,
        0, 0, 0, 1,
      ]);
    };

    const rotateY = (angle: number) => {
      const s = Math.sin(angle);
      const c = Math.cos(angle);
      return new Float32Array([
        c, 0, s, 0,
        0, 1, 0, 0,
        -s, 0, c, 0,
        0, 0, 0, 1,
      ]);
    };

    const translate = (z: number) => new Float32Array([
      1, 0, 0, 0,
      0, 1, 0, 0,
      0, 0, 1, 0,
      0, 0, z, 1,
    ]);

    const vertexSrc = `
      attribute vec3 position;
      attribute vec2 uv;
      uniform mat4 uProj;
      uniform mat4 uView;
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = uProj * uView * vec4(position, 1.0);
      }
    `;
    const fragmentSrc = `
      precision mediump float;
      varying vec2 vUv;
      uniform sampler2D uTex;
      void main() {
        gl_FragColor = texture2D(uTex, vUv);
      }
    `;

    const compileShader = (src: string, type: number) => {
      const shader = gl.createShader(type);
      if (!shader) throw new Error('Failed to create shader');
      gl.shaderSource(shader, src);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        throw new Error(gl.getShaderInfoLog(shader) || 'Shader compile error');
      }
      return shader;
    };

    const createProgram = (vsSource: string, fsSource: string) => {
      const vs = compileShader(vsSource, gl.VERTEX_SHADER);
      const fs = compileShader(fsSource, gl.FRAGMENT_SHADER);
      const prog = gl.createProgram();
      if (!prog) throw new Error('Failed to create program');
      gl.attachShader(prog, vs);
      gl.attachShader(prog, fs);
      gl.linkProgram(prog);
      if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
        throw new Error(gl.getProgramInfoLog(prog) || 'Program link error');
      }
      return prog;
    };

    const getAccessorInfo = (index: number) => {
      const accessor = gltf.accessors[index];
      const view = gltf.bufferViews[accessor.bufferView];
      return { accessor, view };
    };

    const setupBuffers = () => {
      const buffer = decodeBuffer(gltf.buffers[0].uri);
      const { accessor: posAccessor, view: posView } = getAccessorInfo(0);
      const { accessor: uvAccessor, view: uvViewDef } = getAccessorInfo(1);
      const { accessor: idxAccessor, view: idxViewDef } = getAccessorInfo(2);

      const positionView = new Float32Array(buffer, posView.byteOffset || 0, posAccessor.count * 3);
      const uvView = new Float32Array(buffer, uvViewDef.byteOffset || 0, uvAccessor.count * 2);
      const indexView = new Uint16Array(buffer, idxViewDef.byteOffset || 0, idxAccessor.count);

      vao = gl.createBuffer();
      if (!vao) throw new Error('Failed to create buffer');
      gl.bindBuffer(gl.ARRAY_BUFFER, vao);
      gl.bufferData(gl.ARRAY_BUFFER, positionView, gl.STATIC_DRAW);

      const uvBuffer = gl.createBuffer();
      if (!uvBuffer) throw new Error('Failed to create buffer');
      gl.bindBuffer(gl.ARRAY_BUFFER, uvBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, uvView, gl.STATIC_DRAW);

      ibo = gl.createBuffer();
      if (!ibo) throw new Error('Failed to create buffer');
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ibo);
      gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, indexView, gl.STATIC_DRAW);

      return { positionView, uvBuffer };
    };

    const setAttributes = (prog: WebGLProgram, uvBuffer: WebGLBuffer) => {
      const posLoc = gl.getAttribLocation(prog, 'position');
      gl.bindBuffer(gl.ARRAY_BUFFER, vao);
      gl.enableVertexAttribArray(posLoc);
      gl.vertexAttribPointer(posLoc, 3, gl.FLOAT, false, 0, 0);

      const uvLoc = gl.getAttribLocation(prog, 'uv');
      gl.bindBuffer(gl.ARRAY_BUFFER, uvBuffer);
      gl.enableVertexAttribArray(uvLoc);
      gl.vertexAttribPointer(uvLoc, 2, gl.FLOAT, false, 0, 0);
    };

    const loadTexture = async (uri: string) => {
      const img = await loadImage(uri);
      const tex = gl.createTexture();
      if (!tex) throw new Error('Failed to create texture');
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      return tex;
    };

    const draw = (prog: WebGLProgram) => {
      resize();
      const aspect = canvas.width / canvas.height;
      const proj = perspective(Math.PI / 3, aspect, 0.1, 10);

      const spinTime = performance.now() * 0.0012;
      const rotY = hovering ? spinTime * 1.6 : spinTime;
      const tiltX = hovering ? -0.15 : -0.25;

      const scale = new Float32Array([
        2, 0, 0, 0,
        0, 2, 0, 0,
        0, 0, 2, 0,
        0, 0, 0, 1,
      ]);

      const model = multiplyMat4(scale, multiplyMat4(rotateY(rotY), rotateX(tiltX)));
      const view = translate(hovering ? -2.6 : -3.2);
      const modelView = multiplyMat4(view, model);

      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
      gl.enable(gl.DEPTH_TEST);
      gl.enable(gl.CULL_FACE);
      gl.cullFace(gl.BACK);

      const projLoc = gl.getUniformLocation(prog, 'uProj');
      const viewLoc = gl.getUniformLocation(prog, 'uView');
      gl.uniformMatrix4fv(projLoc, false, proj);
      gl.uniformMatrix4fv(viewLoc, false, modelView);

      gl.drawElements(gl.TRIANGLES, gltf.accessors[2].count, gl.UNSIGNED_SHORT, 0);
      raf = requestAnimationFrame(() => draw(prog));
    };

    (async () => {
      try {
        program = createProgram(vertexSrc, fragmentSrc);
        gl.useProgram(program);
        const { uvBuffer } = setupBuffers();
        setAttributes(program, uvBuffer);

        texture = await loadTexture(gltf.images[0].uri);
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, texture);
        const texLoc = gl.getUniformLocation(program, 'uTex');
        gl.uniform1i(texLoc, 0);
        draw(program);
      } catch (err: any) {
        setError(err?.message || 'Failed to render glTF');
      }
    })();

    return () => {
      if (raf) cancelAnimationFrame(raf);
      if (texture) gl.deleteTexture(texture);
      if (ibo) gl.deleteBuffer(ibo);
      if (vao) gl.deleteBuffer(vao);
      if (program) gl.deleteProgram(program);
    };
  }, [card]);

  if (error) {
    return (
      <div className={`flex h-64 items-center justify-center rounded-xl border border-white/15 bg-white/5 text-sm text-rose-200 ${className}`}>
        {error}
      </div>
    );
  }

  return (
    <div
      className={`relative overflow-hidden rounded-xl border border-white/10 bg-gradient-to-br from-slate-900/60 via-slate-800/40 to-slate-900/60 p-2 transition-transform duration-300 ${className}`}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/8 via-transparent to-white/6" />
      <canvas ref={canvasRef} className="h-72 w-full" />
    </div>
  );
}
